import { defineStore } from "pinia";
import { ref } from "vue";
import { fetchForms, saveForm, duplicateFormAdapter, renameFormAdapter, archiveFormAdapter, } from "../infrastructure/adapters/collecteService";
/**
 * Page size used by the store's SINGLE-PAGE sweeps — the archive dialog's redirect-target pool
 * ({@link fetchPublishedForms}) and the campaign pickers' taken-campaign set
 * ({@link fetchTakenCampaignIds}). It is the endpoint's documented CEILING, not a preference:
 * `ListDonationFormsController` refuses `perPage > 100` with a 400 `Form.Pagination.Invalid`
 * (R24 caps every list at 100), so it is a property of the API rather than of either caller —
 * which is why both share it and why neither can raise it to see further. Each sweep reports its
 * own `truncated` instead.
 */
export const LIST_FORMS_MAX_PER_PAGE = 100;
/**
 * Donation-form admin store (setup-store, mirrors finance's `useDonationStore`). Holds the
 * paginated list of forms plus loading/error/pagination state, and exposes `loadForms` /
 * `createForm` for the list + create views.
 */
export const useDonationFormStore = defineStore("collecteDonationForm", () => {
    const forms = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const currentPage = ref(1);
    const perPage = ref(20);
    async function loadForms(params) {
        const resolvedPage = params?.page ?? currentPage.value;
        const resolvedPerPage = params?.perPage ?? perPage.value;
        isLoading.value = true;
        error.value = null;
        try {
            const page = await fetchForms({
                page: resolvedPage,
                perPage: resolvedPerPage,
                campaignId: params?.campaignId,
            });
            // The store keeps its own state names (`totalItems` / `perPage`, read by the list view and
            // sent back as request params); the page comes back in the standard `PaginatedResult`
            // shape, hence `totalCount` / `pageSize` on the right-hand side.
            forms.value = page.items;
            totalItems.value = page.totalCount;
            totalPages.value = page.totalPages;
            currentPage.value = page.page;
            perPage.value = page.pageSize;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "collecte.forms.loadError";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function createForm(payload) {
        isSaving.value = true;
        error.value = null;
        try {
            return await saveForm(payload);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "collecte.forms.saveError";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * Duplicates an existing form via the adapter and returns the new id (CreatedAck). Mirrors
     * `createForm`: toggles `isSaving`, resets/sets `error`, and rethrows on failure so the modal can
     * map the backend `errorCode` (409 slug conflict, 422 slug/title, 404 source gone) to a localized
     * message (R70).
     */
    async function duplicateForm(id, body) {
        isSaving.value = true;
        error.value = null;
        try {
            return await duplicateFormAdapter(id, body);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "collecte.forms.saveError";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * Renames a form's admin label (title only — the `publicSlug` is never touched) and patches the
     * matching list row in place of a refetch. Patching is safe here because the backend list is
     * ordered by id, so a rename can never move a row between pages; a refetch would only cost a
     * round-trip and reset the scroll.
     *
     * The row is patched from the SERVER's echoed `title`, never the submitted string: the domain
     * trims, so the two differ whenever the manager leaves stray whitespace, and the response is the
     * authority. The array is REASSIGNED with a new row object rather than mutated in place — OPAL's
     * DataTable is @tanstack/vue-table, whose row model is memoised on the `data` reference, so an
     * in-place `row.title = …` can leave the stale title on screen.
     *
     * Mirrors `duplicateForm` for the rest: toggles `isSaving`, resets/sets `error`, and rethrows so
     * the modal can map the backend `errorCode` (409 archived, 404 gone, 422 title) to a localized
     * message (R70).
     */
    async function renameForm(id, title) {
        isSaving.value = true;
        error.value = null;
        try {
            const updated = await renameFormAdapter(id, title);
            forms.value = forms.value.map((f) => (f.id === id ? { ...f, title: updated.title } : f));
            return updated;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "collecte.forms.saveError";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    /**
     * The tenant's PUBLISHED forms — the pool the archive dialog offers as a redirect target.
     *
     * Writes NO store state: not `forms`, `currentPage`, `perPage`, `totalItems`, `isLoading` or
     * `error`. Opening the archive dialog must not disturb the table behind it — reusing `loadForms`
     * would repaginate the visible list to page 1 and flip it into its loading skeleton.
     *
     * ONE page at the endpoint's maximum size, because the list endpoint exposes no status filter
     * (only `page` / `perPage` / `campaignId`), so narrowing to Published is necessarily client-side.
     * A tenant holding more than {@link LIST_FORMS_MAX_PER_PAGE} forms therefore sees only that
     * page's published rows as candidates — a documented bound, not an oversight. The proper fix is a
     * `status` filter upstream, which this ticket does not own.
     *
     * The result is deliberately NOT narrowed to a particular archive: the caller excludes the form
     * being archived (the backend refuses a self-redirect with 422
     * `Form.RedirectToFormId.SelfReference`, and a Published source IS in its own candidate pool).
     * Keeping the pool caller-independent means a list fetched for one row stays correct for the
     * next one, so a late response can never smuggle a row's own id back into its options.
     */
    async function fetchPublishedForms() {
        const page = await fetchForms({ page: 1, perPage: LIST_FORMS_MAX_PER_PAGE });
        return {
            forms: page.items.filter((form) => form.status === "Published"),
            // Reporting the truncation is not a nicety. The upstream sorts by `id ASC` — UUID order — so
            // the inspected page is an ARBITRARY slice, and a tenant whose published forms all sort past
            // it would otherwise be told "no other published form is available", which is simply false.
            // Believing it, the admin archives with no destination, and archiving is terminal: no
            // endpoint can ever set the redirect afterwards.
            truncated: page.totalCount > LIST_FORMS_MAX_PER_PAGE,
        };
    }
    /**
     * The campaign ids the tenant's LIVE forms already carry, for the campaign pickers.
     *
     * Writes NO store state, for the same reason as {@link fetchPublishedForms}: a picker priming its
     * options must not repaginate the visible list nor flip it into its loading skeleton.
     *
     * CONSTRAINT: the client-side narrowing MUST mirror `existsByCampaign` exactly — a campaign is
     * taken by a form whose `campaignId` is that campaign AND whose status is NOT `Archived`. Widen
     * it and a genuinely free campaign is greyed out, which the manager experiences as a refusal with
     * no error anywhere to explain it. The narrowing is client-side because the list endpoint exposes
     * no status filter.
     *
     * ONE page at the endpoint's maximum size, so `truncated` is returned rather than swallowed;
     * {@link useTakenCampaigns} owns what callers must do with it.
     */
    async function fetchTakenCampaignIds() {
        const page = await fetchForms({ page: 1, perPage: LIST_FORMS_MAX_PER_PAGE });
        const campaignIds = new Set();
        for (const form of page.items) {
            if (form.campaignId && form.status !== "Archived")
                campaignIds.add(form.campaignId);
        }
        return { campaignIds, truncated: page.totalCount > LIST_FORMS_MAX_PER_PAGE };
    }
    /**
     * Archives a form — the TERMINAL lifecycle transition — optionally nominating the form donors
     * are redirected to afterwards. Mirrors `duplicateForm`: toggles `isSaving`, resets/sets `error`,
     * and rethrows so the caller can map the backend `errorCode` (409 `Form.InvalidTransition`, 422
     * `Form.RedirectToFormId.Invalid` / `.SelfReference`, 404 `Form.NotFound`) to a localized message
     * (R70).
     *
     * On success the archived row is refreshed IN PLACE from the response, and two details of how are
     * load-bearing:
     *
     *  - only `status` is taken, because it is the only field an archive mutates that a list row
     *    shows, and the response is a DIFFERENT shape: `DonationFormRead` carries no `createdAt`
     *    (so assigning it over the row blanks the creation-date column) while it does carry the
     *    wizard's `branding` / `settings` / child collections (so spreading it smuggles fields onto a
     *    summary row that no list code expects). Neither merge direction is safe; a field-by-field
     *    copy of what actually changed is.
     *  - the ARRAY is replaced (`map`), never the slot (`forms.value[i] = …`). The table's row model
     *    is memoized on the data array's identity, so assigning a fresh object into an unchanged
     *    array leaves the memo serving rows whose `original` is still the pre-archive object — the
     *    row would keep reading "Published" for ever, with no error anywhere. Mutating the element in
     *    place would also work (the element is a reactive proxy), but the array swap is correct under
     *    both mechanisms and matches how `loadForms` already writes.
     */
    async function archiveForm(id, redirectToFormId) {
        isSaving.value = true;
        error.value = null;
        try {
            const updated = await archiveFormAdapter(id, redirectToFormId);
            // Matched on the id the CALLER named, not on the response's: the row to refresh is the one
            // the admin acted on, and keying off a response field would turn a contract change into a
            // silent no-op refresh rather than a loud failure.
            forms.value = forms.value.map((form) => form.id === id ? { ...form, status: updated.status } : form);
            return updated;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "collecte.forms.saveError";
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    return {
        forms,
        isLoading,
        isSaving,
        error,
        totalItems,
        totalPages,
        currentPage,
        perPage,
        loadForms,
        createForm,
        duplicateForm,
        renameForm,
        fetchPublishedForms,
        fetchTakenCampaignIds,
        archiveForm,
    };
});
